'use client'
import { useEffect, useRef, type ReactNode } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import styles from './offer-dialog.module.css'

/** Offer review/inbox shell uses the same tokens, sizing and scrolling as offer creation. */
export default function OfferDialog({
	title,
	description,
	children,
	footer,
	onClose,
	busy = false,
	compact = false,
	narrow = false,
	headerDescription = false,
	headerAccessory,
	headerPrefix
}: {
	title: string
	description: string
	children: ReactNode
	footer?: ReactNode
	onClose: () => void
	busy?: boolean
	compact?: boolean
	narrow?: boolean
	headerDescription?: boolean
	headerPrefix?: ReactNode
	headerAccessory?: ReactNode
}) {
	const ref = useRef<HTMLDivElement>(null)
	const titleRef = useRef<HTMLHeadingElement>(null)
	useEffect(() => {
		const viewport = window.visualViewport
		if (!viewport) return
		const sync = () => {
			const style = ref.current?.style
			style?.setProperty('--offer-viewport-width', `${viewport.width}px`)
			style?.setProperty('--offer-viewport-height', `${viewport.height}px`)
			style?.setProperty('--offer-center-x', `${viewport.offsetLeft + viewport.width / 2}px`)
			style?.setProperty('--offer-center-y', `${viewport.offsetTop + viewport.height / 2}px`)
		}
		sync()
		viewport.addEventListener('resize', sync)
		viewport.addEventListener('scroll', sync)
		return () => {
			viewport.removeEventListener('resize', sync)
			viewport.removeEventListener('scroll', sync)
		}
	}, [])
	return (
		<Dialog.Root
			open
			// While the wallet is asked to sign, the native Ordinals Wallet unlock
			// renders as its own modal: drop the focus trap and pointer lock so
			// the buyer can type their password instead of being held here.
			modal={!busy}
			onOpenChange={(open) => {
				if (!open && !busy) onClose()
			}}
		>
			<Dialog.Portal>
				{/* Radix renders no overlay for a non-modal dialog, so keep our own
				    backdrop up while the wallet is signing. */}
				{busy ? (
					<div className={styles.overlay} aria-hidden />
				) : (
					<Dialog.Overlay className={styles.overlay} />
				)}
				<Dialog.Content
					ref={ref}
					className={`${styles.dialog} ${compact ? styles.compactDialog : ''} ${narrow ? styles.narrowDialog : ''}`}
					onOpenAutoFocus={(e) => {
						e.preventDefault()
						titleRef.current?.focus()
					}}
					onEscapeKeyDown={(e) => {
						if (busy) e.preventDefault()
					}}
					onPointerDownOutside={(e) => {
						if (busy || (document.activeElement instanceof HTMLSelectElement && ref.current?.contains(document.activeElement))) e.preventDefault()
					}}
				>
					<div className={styles.form} aria-busy={busy}>
						<header className={`${styles.header} ${headerDescription ? styles.headerWithoutDivider : ''}`}>
							<div className={headerAccessory || headerPrefix ? `${styles.headerWithAccessory} ${headerPrefix ? styles.headerWithCollection : ''}` : undefined}>
							{headerPrefix}
							<Dialog.Title tabIndex={-1} ref={titleRef}>
								{title}
							</Dialog.Title>
                            {headerAccessory}
							{headerDescription && <Dialog.Description className={description ? styles.hint : 'sr-only'}>{description || title}</Dialog.Description>}
							</div>
							<Dialog.Close className={styles.close} disabled={busy} aria-label="Close offers">
								<X size={18} />
							</Dialog.Close>
						</header>
						<div className={styles.body}>
							{!headerDescription && <Dialog.Description className={description ? styles.hint : 'sr-only'}>{description || title}</Dialog.Description>}
							{children}
						</div>
						{footer && <footer className={styles.footer}>{footer}</footer>}
					</div>
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	)
}
